import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
import { ResponsibilityLevelSchema } from '../responsibilities/levels.js';
import { ExternalIdentityInviteSchema, ExternalIdentitySchema } from './telegram.js';
import { LanguageSchema } from '../common/language.js';
import { UserLifecycleStatusSchema } from '../common/userStatus.js';
import { ProjectPermissionsSchema } from '../auth/responses.js';
// Response schemas for the users feature. Field-exposure rule (ANATOMY §L7):
// each shape carries the minimum the caller needs. Never expose password_hash,
// mfa_secret, token_hash, or lockout internals — those are not on any user
// wire shape by construction.
// Account status — DERIVED server-side from existing `users` columns (never a
// stored column): disabled > locked (locked_until>now) > pending
// (must_change_password & never-logged-in) > active. The raw lockout internals
// (locked_until, must_change_password) stay off the wire; only the verdict ships.
export const UserStatusSchema = z.enum(['active', 'pending', 'locked', 'disabled']);
// Chip 1 — how this account signs in on the WEB. DERIVED server-side, never
// stored (ADR-003 §Federated identity): 'sso' (a user_federated_identities row)
// > 'local' (a password hash) > 'invited' (an unused, unexpired web INVITATION —
// the set-your-own-password link is out) > 'invite_expired' (the invitation
// lapsed; visible until re-sent, so admins can see who to chase) > 'none' (no
// web access — and deliberately NOT a claim about channels: the Telegram chip is
// the other truth). Secrets/identities stay off every wire shape (ANATOMY §L7).
export const SignInSchema = z.enum(['local', 'sso', 'invited', 'invite_expired', 'none']);
// Chip 2 — the TELEGRAM link, per project view (identity is global, reachability
// is per-bot): 'linked' (this project's bot can message them) · 'unreachable'
// (linked, but blocked the bot / deleted the chat — the reachability belief) ·
// 'invite_sent' / 'invite_expired' (the CHAT invite — a different invitation
// than chip 1's) · 'not_linked'. Null on a row = the project has no bot (the
// surface hides the column). The platform list carries the global state and
// never says 'unreachable' (reachability is per-project).
export const TelegramLinkStateSchema = z.enum(['linked', 'unreachable', 'invite_sent', 'invite_expired', 'not_linked']);
// Last activity — DERIVED from the per-channel stamps the platform already
// keeps (web = auth_sessions.last_seen_at per request · telegram =
// user_bot_chats.last_seen_at, bumped on every resolved bot message, any bot ·
// api = the PAT's last_used_at). Distinct from `last_login_at` (a SIGN-IN
// fact, web only, stamped at login): this is "when were they last seen at all,
// and where" — the people-management truth for a channel-first fleet.
export const LastActiveChannelSchema = z.enum(['web', 'telegram', 'api']);
// The person's scope as the list, its filter and the export say it — ONE derived fact per
// row (ADR-050 §Consequences): an administrator sees everything by definition; the whole
// project (scope_mode 'all'); specific places (at least one grant row of any kind); no
// scope yet (none). The words: Administrator · Whole project · Specific places · No scope yet.
export const ScopeKindSchema = z.enum(['admin', 'whole', 'specific', 'none']);
// ── GET /api/projects/:projectId/users — in-project member list row ─────────
// Joins users × user_project_roles × roles for THIS project (strict isolation).
export const ProjectUserListItemSchema = z
    .object({
    row_version: RowVersionSchema,
    user_id: z.string().max(16),
    username: z.string().max(100),
    first_name: z.string().max(64),
    last_name: z.string().max(64),
    email: z.string().max(128).nullable(),
    is_disabled: z.boolean(),
    is_project_admin: z.boolean(),
    role_id: z.number().int().nullable(),
    role_name: z.string().max(32).nullable(),
    scope_kind: ScopeKindSchema,
    status: UserStatusSchema,
    sign_in: SignInSchema,
    telegram: TelegramLinkStateSchema.nullable(),
    // The most recent activity across channels + which channel it was (null =
    // never seen anywhere). See LastActiveChannelSchema.
    last_active_at: z.string().datetime().nullable(),
    last_active_channel: LastActiveChannelSchema.nullable(),
})
    .strict();
// The person's scope as the Access card, the peek, the editor and the wizard's review read it
// (ADR-050 §2 · §3): per section, the "All …" state, the picks INSIDE the viewer's own reach by
// name (the highest picks — a zone absorbs its clusters and sites), and a COUNT of the grants
// outside it — kept untouched by any edit of this viewer, never named (ADR-050 §1 · ADR-066 §9).
// `vehicle_pools` is null while the vehicles module is off; `teams` while the field-force module is off. An administrator carries kind
// 'admin' and empty sections; the whole project carries kind 'whole' and every "all" true.
const namedPlace = z.object({ id: z.string().max(16), name: z.string() }).strict();
export const UserScopeReadSchema = z
    .object({
    kind: ScopeKindSchema,
    /** What the VIEWER may switch to "All …" — a giver grants all of a kind only when they hold all of it
     *  (ADR-050 §3); the editor disables the switch otherwise (an "All …" the viewer may not grant is read, not edited).
     *  `whole`: the viewer is project-wide — the last "All" that would make the whole project needs it. */
    giver: z.object({ sites: z.boolean(), depots: z.boolean(), vehicle_pools: z.boolean(), teams: z.boolean(), whole: z.boolean() }).strict(),
    sites: z
        .object({
        all: z.boolean(),
        zones: z.array(z.object({ id: z.number().int(), name: z.string() }).strict()),
        clusters: z.array(z.object({ id: z.number().int(), name: z.string(), zone_id: z.number().int() }).strict()),
        sites: z.array(z.object({ id: z.string().max(16), name: z.string(), reference: z.string().nullable() }).strict()),
        outside: z.object({ zones: z.number().int(), clusters: z.number().int(), sites: z.number().int() }).strict(),
    })
        .strict(),
    depots: z.object({ all: z.boolean(), items: z.array(namedPlace), outside: z.number().int() }).strict(),
    vehicle_pools: z.object({ all: z.boolean(), items: z.array(namedPlace), outside: z.number().int() }).strict().nullable(),
    /** Team scope (ADR-083 §4): the teams the person may see and run with a team key — never their memberships. */
    teams: z.object({ all: z.boolean(), items: z.array(namedPlace), outside: z.number().int() }).strict().nullable(),
})
    .strict();
// What the person answers for — one of their chains (ADR-050 §2, "Answers for"): the
// responsibility, its level, the place. A place outside the viewer's reach is named by its
// kind alone (`scope_label` null — ADR-066 §9); the project level reads "Project".
export const PersonChainSchema = z
    .object({
    responsibility_id: z.number().int(),
    responsibility_name: z.string(),
    level: ResponsibilityLevelSchema,
    scope_id: z.string(),
    scope_label: z.string().nullable(),
})
    .strict();
export const ProjectUsersListResponseSchema = z
    // has_telegram_bot: the PROJECT-level fact the Telegram column hides on — said
    // once on the envelope, never repeated per row (rows carry null then).
    .object({ items: z.array(ProjectUserListItemSchema), has_telegram_bot: z.boolean() })
    .strict();
// ── GET /api/projects/:projectId/users/:userId — in-project detail ──────────
// Identity (read-only on this surface) + membership (editable). Powers the
// in-project user edit form. Phone included for admin display; still no secrets.
export const ProjectUserDetailSchema = z
    .object({
    row_version: RowVersionSchema,
    user_id: z.string().max(16),
    username: z.string().max(100),
    first_name: z.string().max(64),
    last_name: z.string().max(64),
    email: z.string().max(128).nullable(),
    phone_e164: z.string().max(16).nullable(),
    language: LanguageSchema,
    is_disabled: z.boolean(),
    is_project_admin: z.boolean(),
    role_id: z.number().int().nullable(),
    role_name: z.string().max(32).nullable(),
    // What the person can see, as the Access card and the editor read it (below).
    scope: UserScopeReadSchema,
    // What the person answers for — their chains, read-only (the card's third verb).
    responsibilities: z.array(PersonChainSchema),
    status: UserStatusSchema,
    sign_in: SignInSchema,
    mfa_enabled: z.boolean(),
    last_login_at: z.string().datetime().nullable(),
    // Last activity, the detail grain: the derived max + channel (as the list)
    // PLUS the per-channel stamps for the breakdown line.
    last_active_at: z.string().datetime().nullable(),
    last_active_channel: LastActiveChannelSchema.nullable(),
    last_active_web_at: z.string().datetime().nullable(),
    last_active_telegram_at: z.string().datetime().nullable(),
    last_active_api_at: z.string().datetime().nullable(),
    // True when this project is the user's ONLY membership. Powers the
    // remove-from-project flow: the FE must know BEFORE acting so it can offer
    // "disable the account too" and run disable→remove in that order (disable is
    // project-scoped and 404s once the membership is gone). A boolean, not a
    // cross-project count — reveals the least (field-exposure rule, ANATOMY §L7).
    is_sole_membership: z.boolean(),
    // Chat-account connection. On the DETAIL read only — the list would
    // pay a per-row join for a fact nobody scans a table for, and the peek/detail is
    // where the connect/disconnect doors live.
    external_identity: ExternalIdentitySchema,
})
    .strict();
// ── GET /api/projects/:projectId/my-access — the caller's standing HERE ─────
// The per-screen capability read (a channel's F7): serialized from the SAME
// narrowed entry the access gate attached to the request — for an on-behalf
// call that is already human ∩ the credential's granted list, entitlements
// applied — so the menu a channel renders IS the object the gate enforces one
// tap later (never a second permission computation). Reuses the /me per-project
// entry shape (single home) + the caller's role name for display. Membership is
// the gate; clients re-ask per screen render and never cache (the server's own
// permission cache bounds staleness; cadence table in DATA-CONTRACTS §Live
// contract — Telegram bot). The entry is the gate's own, so the /me-only facts
// (`participates` — the web menu's, asked on /me alone) are not part of it.
export const MyAccessResponseSchema = ProjectPermissionsSchema.omit({ participates: true }).extend({
    /** The caller's role name in this project; null for a project admin. */
    role_name: z.string().max(32).nullable(),
}).strict();
// ── GET /api/projects/:projectId/users/search?q=… — global-pool result ──────
// `in_project` lets the frontend grey out users already attached (matches dev's
// AssociateUserModal UX) rather than hiding them.
export const UserSearchResultSchema = z
    .object({
    id: z.string().max(16),
    username: z.string().max(100),
    first_name: z.string().max(64),
    last_name: z.string().max(64),
    email: z.string().max(128).nullable(),
    /** The account's lifecycle — the drawer offers "Reactivate too" for a disabled one (ADR-081 §6). */
    status: UserLifecycleStatusSchema,
    /** In THIS project today. */
    in_project: z.boolean(),
    /** Left THIS project (an ended row — ADR-081 §6): the date, and their last role for the add-back's pre-fill
     *  (null when they were an administrator). Both null when active here or never a member. */
    left_at: z.string().nullable(),
    last_role_id: z.number().int().nullable(),
})
    .strict();
// `has_more` is true when the pool held more matches than the returned cap —
// the picker shows a "keep typing to narrow" hint instead of silently hiding
// the 21st match. The pool is search-first (q required); no offset/cursor.
export const UserSearchResponseSchema = z
    .object({ items: z.array(UserSearchResultSchema), has_more: z.boolean() })
    .strict();
// The invitation as the admin sees it ONCE (create response + the grant action):
// the raw link (the only plaintext copy — the copy-link delivery), its expiry,
// and which couriers actually carried it. Mirrors the chat invite's issue
// response (that surface already hands the admin a credentialed link).
export const WebInviteSchema = z
    .object({
    link: z.string().max(512),
    expires_at: z.string().datetime(),
    email_sent: z.boolean(),
    telegram_sent: z.boolean(),
})
    .strict();
// ── POST create-and-associate / associate — id (+ the invitations, once) ─────
export const CreatedUserResponseSchema = z
    .object({
    user_id: z.string().max(16),
    // null = the web square was off (no credential minted); associate: null too.
    invite: WebInviteSchema.nullable(),
    // The chat invite minted with the account when telegram_invite was asked —
    // null when not asked, no bot, or the mint failed (never fails the create;
    // asked-but-null reads as "send it from their page"). Associate: null.
    chat_invite: ExternalIdentityInviteSchema.nullable(),
})
    .strict();
// ── POST …/users/:userId/grant-web-access (the reset route's successor verb) ──
// kind 'invite' = the target had no password (first credential); 'reset' = a
// recovery link for an existing one. Same set-password page either way.
export const GrantWebAccessResponseSchema = z
    .object({
    kind: z.enum(['invite', 'reset']),
    link: z.string().max(512),
    expires_at: z.string().datetime(),
    email_sent: z.boolean(),
    telegram_sent: z.boolean(),
})
    .strict();
// ── DELETE remove-from-project — remaining project count powers the
// "this was their last project — also disable?" prompt (0 ⇒ prompt). ──────────
export const RemoveUserResponseSchema = z
    .object({ remaining_projects: z.number().int().nonnegative() })
    .strict();
// ── GET …/users/:userId/impact — what a departure or a disable touches (ADR-081 §4–§5) ──
// The chains the person holds HERE, each with their position, who remains and whether
// nobody would (the leave dialog asks place by place; the disable dialog warns); the
// personal rows the leave ends; the project's live tokens; and — for a sysadmin, whose
// disable reaches every project — the chains held elsewhere, per project.
export const DepartureChainSchema = PersonChainSchema.extend({
    /** Lead = first in the chain's order; the next moves up when a lead leaves. */
    position: z.enum(['lead', 'backup']),
    /** The other ACTIVE accounts on this chain (a disabled holder keeps their place but answers nothing). */
    remaining_active: z.number().int().nonnegative(),
    /** Nobody active remains once the person leaves — the place is left with no one. */
    alone: z.boolean(),
}).strict();
export const DepartureImpactSchema = z
    .object({
    chains: z.array(DepartureChainSchema),
    scope_rows: z.number().int().nonnegative(),
    follows: z.number().int().nonnegative(),
    saved_views: z.number().int().nonnegative(),
    /** The teams the person is a member of — the leave ends every membership (ADR-081 §3 · ADR-083 §2). */
    teams: z.array(z.object({ id: z.string().max(16), name: z.string() }).strict()),
    /** Personal access tokens pinned to this project, still live — revoked by the leave. */
    tokens: z.number().int().nonnegative(),
    /** Sysadmin only (a disable reaches every project): the chains held in OTHER projects. Empty otherwise. */
    elsewhere: z.array(z.object({ project_id: z.string(), name: z.string(), chains: z.number().int().positive() }).strict()),
})
    .strict();
// ── GET /api/platform/users — sysadmin cross-project list row ───────────────
export const PlatformUserListItemSchema = z
    .object({
    id: z.string().max(16),
    username: z.string().max(100),
    first_name: z.string().max(64),
    last_name: z.string().max(64),
    email: z.string().max(128).nullable(),
    is_sysadmin: z.boolean(),
    is_disabled: z.boolean(),
    // Raw account lifecycle (active | pending_activation | disabled) — distinct from
    // the project-member UserStatus. `pending_activation` is the SSO JIT holding state
    // that the platform Users queue acts on (ADR-054). is_disabled kept for convenience.
    status: UserLifecycleStatusSchema,
    sign_in: SignInSchema,
    // Global link state (no per-project reachability at platform altitude).
    telegram: TelegramLinkStateSchema,
    last_login_at: z.string().datetime().nullable(),
    project_count: z.number().int().nonnegative(),
})
    .strict();
export const PlatformUsersListResponseSchema = z
    .object({ items: z.array(PlatformUserListItemSchema) })
    .strict();
// ── POST /api/platform/users — minimal id ───────────────────────────────────
export const PlatformCreatedUserResponseSchema = z
    .object({ id: z.string().max(16) })
    .strict();
